/**
 * Moving the focus without a mouse or a touch screen (a gamepad's D-pad and stick): to the
 * nearest control in the pressed direction (spatial navigation), inside a scope — the open
 * dialog or menu, else the whole page — and pressing the focused control.
 *
 * - The first move in a scope where nothing is focused yet puts the focus where the scope
 *   starts (PAD_START_ATTR: the room's «Я на старте!», a dialog's content), without moving on.
 * - The nearest control in a direction is the one ahead of the focused one (past its far
 *   edge; failing that, one that overlaps it in line, by the centres) with the smallest
 *   distance: the gap along the direction plus the sideways gap, weighted so that left / right
 *   stays in its row. Nothing ahead: the focus stays.
 * - A slider takes left / right as its own (a step of its value); a text field never keeps
 *   the focus (typing needs a keyboard anyway).
 */

export type Direction = 'up' | 'down' | 'left' | 'right';

/**
 * Where a pad's focus starts in a screen or a dialog: a control, or a container whose first
 * control it is.
 */
export const PAD_START_ATTR = 'data-pad-start';

const FOCUSABLE = 'button, a[href], input:not([type="hidden"]), select, textarea, [tabindex]';

/** Ahead means past the far edge; this much overlap (CSS px) still counts. */
const OVERLAP = 4;
/** Sideways distance costs this much more than distance ahead: rows and columns hold. */
const ACROSS_WEIGHT: Readonly<Record<Direction, number>> = {
  up: 2,
  down: 2,
  left: 30,
  right: 30,
};
/** Among equally near controls, the one whose centre is more in line wins. */
const CENTRE_WEIGHT = 0.05;

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface Candidate {
  element: HTMLElement;
  box: Box;
}

/** Where the focus may go: the topmost open modal (a dialog, the in-game menu), else the page. */
export function navigationScope(doc: Document): Element {
  const modals = doc.querySelectorAll('[aria-modal="true"]');
  for (let i = modals.length - 1; i >= 0; i--) {
    const modal = modals[i]!;
    if (!modal.closest('[inert]')) return modal;
  }
  return doc.body;
}

/**
 * Moves the focus from the focused control of `scope` to the nearest one in `direction`;
 * with none focused, to where the scope starts. Returns the control that has the focus
 * afterwards (null: the scope has none).
 */
export function moveFocus(scope: Element, direction: Direction): HTMLElement | null {
  const candidates = controlsIn(scope);
  const current = focusedAmong(scope, candidates);
  if (!current) return focusStart(scope, candidates);
  const element = current.element;
  if (isSlider(element) && (direction === 'left' || direction === 'right')) {
    stepSlider(element, direction === 'right' ? 1 : -1);
    return element;
  }
  const next = nearest(current, candidates, direction);
  if (next) focus(next.element);
  return next?.element ?? element;
}

/**
 * Presses the focused control of `scope` (a click, as a tap would); with none focused, only
 * puts the focus where the scope starts. Text fields and sliders are left as they are.
 */
export function pressFocused(scope: Element): HTMLElement | null {
  const candidates = controlsIn(scope);
  const current = focusedAmong(scope, candidates);
  if (!current) return focusStart(scope, candidates);
  const element = current.element;
  if (!isTextField(element) && !isSlider(element)) element.click();
  return element;
}

/** The controls of `scope` the focus can go to, in reading order, with their boxes. */
function controlsIn(scope: Element): Candidate[] {
  const found: Candidate[] = [];
  for (const element of scope.querySelectorAll<HTMLElement>(FOCUSABLE)) {
    if (!canFocus(element)) continue;
    const box = boxOf(element);
    // Nothing on screen (display: none, collapsed): not a place to go.
    if (box.right - box.left <= 0 || box.bottom - box.top <= 0) continue;
    found.push({ element, box });
  }
  return found;
}

function canFocus(element: HTMLElement): boolean {
  if (element.tabIndex < 0 || element.matches(':disabled')) return false;
  return !element.closest('[inert], [hidden], [aria-hidden="true"]');
}

/**
 * The box a player sees: a radio or a checkbox hidden behind its tile (a segmented choice, a
 * picker) is the tile, its label.
 */
function boxOf(element: HTMLElement): Box {
  const label =
    element instanceof HTMLInputElement && (element.type === 'radio' || element.type === 'checkbox')
      ? element.closest('label')
      : null;
  const rect = (label ?? element).getBoundingClientRect();
  return { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom };
}

function focusedAmong(scope: Element, candidates: readonly Candidate[]): Candidate | null {
  const active = scope.ownerDocument.activeElement;
  if (!active || !scope.contains(active)) return null;
  return candidates.find((candidate) => candidate.element === active) ?? null;
}

/** Focuses the scope's start: the first control in a PAD_START_ATTR element, else the first one. */
function focusStart(scope: Element, candidates: readonly Candidate[]): HTMLElement | null {
  let start: Candidate | undefined;
  for (const marker of scope.querySelectorAll(`[${PAD_START_ATTR}]`)) {
    start = candidates.find((c) => marker === c.element || marker.contains(c.element));
    if (start) break;
  }
  start ??= candidates[0];
  if (start) focus(start.element);
  return start?.element ?? null;
}

function nearest(
  from: Candidate,
  candidates: readonly Candidate[],
  direction: Direction,
): Candidate | null {
  // Strictly ahead first; then controls that overlap the focused one, in line with it.
  for (const strict of [true, false]) {
    let best: Candidate | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const candidate of candidates) {
      if (candidate === from) continue;
      const d = distance(from.box, candidate.box, direction, strict);
      if (d !== null && d < bestDistance) {
        best = candidate;
        bestDistance = d;
      }
    }
    if (best) return best;
  }
  return null;
}

/** How far `to` is from `from` going in `direction`; null if it is not that way. */
function distance(from: Box, to: Box, direction: Direction, strict: boolean): number | null {
  const vertical = direction === 'up' || direction === 'down';
  const sign = direction === 'down' || direction === 'right' ? 1 : -1;
  // Along the direction: the gap between the facing edges, and how far the centre is ahead.
  const along = vertical
    ? sign > 0
      ? to.top - from.bottom
      : from.top - to.bottom
    : sign > 0
      ? to.left - from.right
      : from.left - to.right;
  const centreAhead = vertical
    ? (sign * (to.top + to.bottom - from.top - from.bottom)) / 2
    : (sign * (to.left + to.right - from.left - from.right)) / 2;
  if (centreAhead <= 1) return null;
  // Across it: how far apart they are sideways (0 when they share the row or the column).
  const across = vertical
    ? gap(from.left, from.right, to.left, to.right)
    : gap(from.top, from.bottom, to.top, to.bottom);
  // Overlapping it counts only by the centres, and only in line (never a jump across the page).
  if (along < -OVERLAP && (strict || across > 0)) return null;
  const offCentre = vertical
    ? Math.abs(to.left + to.right - from.left - from.right) / 2
    : Math.abs(to.top + to.bottom - from.top - from.bottom) / 2;
  return Math.max(0, along) + across * ACROSS_WEIGHT[direction] + offCentre * CENTRE_WEIGHT;
}

/** The gap between two ranges (0 when they overlap). */
function gap(a1: number, a2: number, b1: number, b2: number): number {
  return Math.max(0, b1 - a2, a1 - b2);
}

function focus(element: HTMLElement): void {
  // Scrolled into view if needed (a long list in a dialog, the profile editor's tiles).
  element.focus();
}

function isSlider(element: HTMLElement): element is HTMLInputElement {
  return element instanceof HTMLInputElement && element.type === 'range';
}

/** Typing needs a keyboard: a pad's A leaves text fields alone. */
function isTextField(element: HTMLElement): boolean {
  if (element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) return true;
  if (element.isContentEditable) return true;
  return (
    element instanceof HTMLInputElement &&
    !['button', 'checkbox', 'radio', 'range', 'reset', 'submit', 'color', 'file', 'image'].includes(
      element.type,
    )
  );
}

/** One step of a slider (on its grid, within its range), told to the page as dragging would. */
function stepSlider(input: HTMLInputElement, direction: 1 | -1): void {
  const min = numberOr(input.min, 0);
  const max = numberOr(input.max, 100);
  const step = numberOr(input.step, 1) || 1;
  const value = numberOr(input.value, min);
  const steps = Math.round((value - min) / step) + direction;
  const next = Math.min(max, Math.max(min, min + steps * step));
  if (next === value) return;
  input.value = String(next);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

function numberOr(text: string, fallback: number): number {
  const value = Number.parseFloat(text);
  return Number.isFinite(value) ? value : fallback;
}
